import api from '../../service/api'
import identity from '../../service/identity'
import bus from '../../service/bus'

/*
	boxart accounts. Guests can play with no account at all; their daily
	progress lives in localStorage. Signing in (geekgroup OIDC, Google, or
	passwordless email) gives a boxart account — its own session token, kept in
	localStorage as `boxart.session` — so streaks and results follow the player
	across devices.

	Login itself happens at auth.geekgroup.app (the geekgroup OIDC flow), via
	Google Identity Services, or by emailed code; this store just exchanges the
	resulting credential for a boxart session and tracks who's signed in.
	See service/oidc.js.
*/

export default {

	namespaced: true,

	state: {
		// { id, name, avatar, admin, ... } when signed in, else null
		account: null,
		// whether the first session check has come back
		checked: false
	},

	getters: {

		account: function(state) {

			return state.account

		},

		signedIn: function(state) {

			return !!state.account

		},

		isAdmin: function(state) {

			return !!(state.account && state.account.admin)

		},

		checked: function(state) {

			return state.checked

		}

	},

	mutations: {

		set: function(state, account) {

			state.account = account || null

		},

		checked: function(state) {

			state.checked = true

		}

	},

	actions: {

		// "who am I" on boot — resolves the stored boxart session token
		recognise: function(context) {

			return api.request('auth/session', { auth: identity.session() }).then(function(json) {

				// a transient network blip must not flip the UI to logged-out
				// while the session token is still valid — keep prior state
				if (!(json && json.error)) context.commit('set', json && json.account)
				context.commit('checked')

				return json

			})

		},

		// finish a geekgroup OIDC sign-in: exchange the id_token for a boxart
		// session + account
		complete: function(context, data) {

			data = data || {}

			// pass the current session: if already signed in (e.g. via Google /
			// email), the backend LINKS geekgroup to that account rather than
			// switching accounts
			return api.request('auth/geekgroup', {
				id_token: data.id_token,
				access_token: data.access_token,
				auth: identity.session()
			}).then(function(json) {

				if (json && json.token) {

					identity.setSession(json.token)
					context.commit('set', json.account)

					// a guest who just signed in: compute the gap of local plays not
					// on the account, then open the sync dialog only if there's
					// something to sync (no empty flash on sign-in). Reads
					// localStorage, so it also works on the post-OIDC load.
					context.dispatch('game/syncGap', null, { root: true }).then(function(gap) {

						if (gap && gap.length) bus.$emit('sync-open', { plays: gap })

					})

				}

				context.commit('checked')

				return json

			})

		},

		// finish a Google sign-in (id_token from Google Identity Services)
		google: function(context, idToken) {

			return api.request('auth/google', { id_token: idToken }).then(function(json) {

				if (json && json.token) {

					identity.setSession(json.token)
					context.commit('set', json.account)

					// a guest who just signed in: compute the gap of local plays not
					// on the account, then open the sync dialog only if there's
					// something to sync (no empty flash on sign-in). Reads
					// localStorage, so it also works on the post-OIDC load.
					context.dispatch('game/syncGap', null, { root: true }).then(function(gap) {

						if (gap && gap.length) bus.$emit('sync-open', { plays: gap })

					})

				}

				return json

			})

		},

		// passwordless email: mail a 6-digit code
		emailSend: function(context, username) {

			return api.request('session/code/send', { username: username })

		},

		// passwordless email: verify the code, sign in
		emailLogin: function(context, data) {

			data = data || {}

			return api.request('session/code/login', { username: data.username, code: data.code }).then(function(json) {

				if (json && json.token) {

					identity.setSession(json.token)
					context.commit('set', json.account)

					// a guest who just signed in: compute the gap of local plays not
					// on the account, then open the sync dialog only if there's
					// something to sync (no empty flash on sign-in). Reads
					// localStorage, so it also works on the post-OIDC load.
					context.dispatch('game/syncGap', null, { root: true }).then(function(gap) {

						if (gap && gap.length) bus.$emit('sync-open', { plays: gap })

					})

				}

				return json

			})

		},

		logout: function(context) {

			api.request('auth/logout', { auth: identity.session() })

			identity.clearSession()
			// also drop the shared geekgroup cookie — otherwise recognise()
			// would still authenticate as the just-logged-out user
			identity.clearGeekgroup()
			context.commit('set', null)

		}

	}

}
